import { Box, Slide, Stack, styled } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolDrawer, PoolDrawerHeader } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { SortingArrowLeftIcon } from '@synergy/theme-next';
import { TransitionGroup } from 'react-transition-group';

import { Category } from '../../../../models/category';
import { CategoryTreeDrawerList } from './category-tree-drawer-list';
import { CategoryTreeMenuProps } from '../menu/category-tree-menu';

const SlideOverBox = styled(Box)(({ theme }) => ({
    position: 'absolute',
    height: `calc(100% - ${theme.spacing(10)})`, // height: 100% - header
    width: '100%',
    top: theme.spacing(10),
    backgroundColor: theme.palette.common.white,
}));

const MenuContainerBox = styled(Box, { shouldForwardProp: prop => prop !== 'active' })<{ active: boolean }>(
    ({ theme, active }) => ({
        height: '100%',
        transition: theme.transitions.create('opacity', {
            duration: theme.transitions.duration.standard,
        }),
        opacity: active ? 1 : 0,
        overflow: 'auto',
    })
);

export const CategoryTreeDrawer = (props: CategoryTreeMenuProps): ReactElement => {
    const [parentCategories, setParentCategories] = useState<(Category | undefined)[]>([undefined]);
    const currentCategory = parentCategories[parentCategories.length - 1];

    const handleCategoryClick = (category: Category): void => {
        setParentCategories([...parentCategories, category]);
    };

    const handleBack = (): void => {
        if (parentCategories.length > 1) {
            const nextParentCategories = [...parentCategories];
            nextParentCategories.pop();
            setParentCategories(nextParentCategories);
        } else {
            props.onClose();
        }
    };

    useEffect(() => {
        // Reset selected category on open
        if (props.isOpen) {
            setParentCategories([undefined]);
        }
    }, [props.isOpen]);

    return (
        <PoolDrawer open={props.isOpen}>
            <Stack>
                <PoolDrawerHeader onClick={handleBack}>
                    <SortingArrowLeftIcon />
                    {currentCategory?.name ?? (
                        <strong>
                            <FormattedMessage id="app.common.back" />
                        </strong>
                    )}
                </PoolDrawerHeader>
                <Box>
                    <TransitionGroup>
                        {parentCategories.map(category => (
                            <Slide key={category?.id ?? -1} direction="left" appear={!!category}>
                                <SlideOverBox>
                                    <MenuContainerBox active={category?.id === currentCategory?.id}>
                                        <CategoryTreeDrawerList
                                            parentId={category?.id}
                                            onCategoryClick={handleCategoryClick}
                                            onSelect={props.onSelect}
                                        >
                                            {props.children}
                                        </CategoryTreeDrawerList>
                                    </MenuContainerBox>
                                </SlideOverBox>
                            </Slide>
                        ))}
                    </TransitionGroup>
                </Box>
            </Stack>
        </PoolDrawer>
    );
};
